// What a reader holds: one shape for every asynchronous read in the product.
//
// WHY THIS IS IN THE CONTRACT PACKAGE AND NOT IN THE WEB APP. ICD-U01 §4
// specified it and nothing implemented it, so the web app grew five versions
// of the same idea — `{preview, loading, unreachable}`, `{view, loading,
// error}`, `{leagues, chores, loading, error}`, `{status:'loading' | 'ready' |
// 'unavailable'}`, and a devices hook with three separate `useState` calls.
// Five shapes for one concept means five chances to collapse the distinction
// that matters, and four of the five had already collapsed part of it.
//
// THE DISTINCTION. "Nothing there" and "we do not know" are different
// answers, and every provider ICD in this project says so. `ready` with a
// null value is a calm empty state — no matchup this week, a code that names
// no league. `failed` means the site is not currently telling the truth, and
// it has to say so. A reader that folds the second into the first has told
// somebody their league is empty because a request did not happen.
//
// `lastValue` is the other half of it. A refresh that fails should not blank
// a screen that already holds good data; it should keep the data and say the
// refresh failed. The reader decides how to show that, which is why the
// snapshot carries both rather than choosing.
//
// This is a TYPE and not a Zod schema. A snapshot never crosses the wire —
// what crosses is the envelope in `envelope.ts`, and a snapshot is what a
// client makes of it, including the two outcomes the wire has no way to
// describe: we could not reach you, and what you sent is not what I know how
// to read.

import type { ErrorCode } from './envelope';

/**
 * Why a read has no value.
 *
 * The wire's `ErrorCode` plus the four things that happen on this side of
 * it. Keeping them in one enum is what lets a screen write a single branch:
 * a person who cannot see their league does not care whether the server
 * refused or the train went into a tunnel, but the sentence they are shown
 * is different, and this is what picks it.
 */
export type FailureCode =
  | ErrorCode
  /** No usable answer arrived: the request threw, or what came back was not our API. */
  | 'unreachable'
  /**
   * No answer at all within the client's limit: the server took the request
   * and never replied. Its own code because a write that timed out may have
   * landed, so it cannot be told "nothing was saved" the way `unreachable`
   * can (the web client's B-133).
   */
  | 'timeout'
  /** An answer arrived and did not match the shape this client knows (ICD-U01 §9). */
  | 'drift'
  /**
   * Drift with a specific, recoverable cause: every mismatch is a value in a
   * closed set that this build has never heard of.
   *
   * Worth its own code because the remedy is different and the client can
   * name it. API.md's compatibility rule is additive-only — fields are added,
   * never removed — so a payload that fails ONLY on unrecognized members is
   * almost certainly a newer server talking to an older build, and "reload to
   * get the newest version" is something a person can act on. "A shape we do
   * not understand" is not.
   */
  | 'app_outdated';

export interface Failure {
  code: FailureCode;
  /** Written for the person, in their language, safe to show as-is. */
  message: string;
}

/**
 * One read, in one of three states (ICD-U01 §4).
 *
 * `asOf` is when the value was true where the payload says so, and when the
 * answer arrived where it does not.
 *
 * `stale` means "this value is on screen while a newer one is being fetched",
 * and the reducer sets it on exactly that path — a re-read over a value
 * already held emits `ready` with `stale: true` rather than `loading`, so a
 * live view does not flash empty. What has NOT arrived is polling and the
 * ICD-U01 §8 freshness budgets: no elapsed-time rule sets this field.
 *
 * This said "`stale` is always false today" until 2026-09-21, having been
 * written before the reducer set it — a docstring asserting a runtime fact on
 * the contract type a second client programs against, contradicted by a test
 * two directories away (AUDIT-2026-09-21 C10).
 */
export type Snapshot<T> =
  | { state: 'loading' }
  | { state: 'ready'; value: T; asOf: string; stale: boolean }
  | { state: 'failed'; failure: Failure; lastValue: T | null; lastAsOf: string | null };

/** The value if we have one, from a successful read or a failed refresh. */
export function valueOf<T>(snapshot: Snapshot<T>): T | null {
  if (snapshot.state === 'ready') return snapshot.value;
  if (snapshot.state === 'failed') return snapshot.lastValue;
  return null;
}

/** The failure if there is one. Never set while a value is being shown as current. */
export function failureOf<T>(snapshot: Snapshot<T>): Failure | null {
  return snapshot.state === 'failed' ? snapshot.failure : null;
}

/**
 * Whether a screen has nothing to draw yet.
 *
 * True only in `loading`, deliberately: a failed refresh that kept its value
 * is not loading, and a screen that treats it as such replaces real data
 * with a spinner.
 */
export function isLoading<T>(snapshot: Snapshot<T>): boolean {
  return snapshot.state === 'loading';
}
